import {createSelector} from 'reselect';
import type {
  MCCCustomViewportHeights,
  MultiContextCanvasState,
  PageFrame,
} from './types';
import {getGuardrailPolicy, type MCCGuardrailsPolicyType} from './guardrails';
import {selectDesignerAbility} from '@packages/systems/designer-abilities';
import {DesignerStores} from '@packages/systems/designer/flux';
import type {BreakpointID} from '@packages/systems/style/types';
import {MCC_RENDER_ORDER, mccFrameIdFor} from './frames';
import {MCC_ALL_BREAKPOINTS_GAP_PX} from './constants';
import {getBreakpointHeight} from '@packages/systems/open-canvas/core';

export {
  MCC_ALL_BREAKPOINTS_GAP_PX,
  MCC_ALL_BREAKPOINTS_VIEWPORT_SCALE,
} from './constants';

type StoreShape = {
  MultiContextCanvasStore: MultiContextCanvasState;
  ExternalStore: {mediaQuery: BreakpointID};
};

/**
 * Returns true if the multi-context canvas is currently active (system state).
 */
export const selectIsEditingPageOnOpenCanvas = (state: StoreShape) =>
  state.MultiContextCanvasStore.isEditingPageOnOpenCanvas;

/**
 * Returns the map of all frames by frame ID.
 */
export const selectFrames = (state: StoreShape) =>
  state.MultiContextCanvasStore.frames;

/**
 * Returns the frame id for the active breakpoint, or `null` if no
 * frame exists yet.
 */
export const selectCurrentBreakpointFrameId = createSelector(
  selectFrames,
  (state: StoreShape) => state.ExternalStore.mediaQuery,
  (frames, breakpointId): string | null =>
    frames[mccFrameIdFor(breakpointId)]?.id ?? null
);

/**
 * Returns the frame for the active breakpoint, or null if none exists.
 */
export const selectCurrentFrame = createSelector(
  selectFrames,
  selectCurrentBreakpointFrameId,
  (frames, currentFrameId): PageFrame | null =>
    currentFrameId ? (frames[currentFrameId] ?? null) : null
);

/**
 * Returns the id of the currently selected frame on MCC, or null if none.
 */
export const selectMCCSelectedFrameId = (state: StoreShape) =>
  state.MultiContextCanvasStore.selectedFrameId;

/**
 * Returns the currently selected `PageFrame`, or null if no frame is selected
 * or the selected frame id does not match a known frame. Use this to drive
 * settings UIs that need the selected frame's breakpoint and width even when
 * the user's active breakpoint differs from the selected frame's breakpoint.
 */
export const selectMCCSelectedFrame = createSelector(
  selectFrames,
  selectMCCSelectedFrameId,
  (frames, selectedFrameId): PageFrame | null =>
    selectedFrameId ? (frames[selectedFrameId] ?? null) : null
);

/**
 * Returns the frame width for the active breakpoint, or `null` if no
 * frame exists yet.
 */
export const selectMCCCurrentFrameWidth = createSelector(
  selectCurrentFrame,
  (frame): number | null => (frame ? frame.width : null)
);

/**
 * Custom MCC viewport heights keyed by breakpoint. Empty when every
 * breakpoint is still on its verified device default.
 */
export const selectMCCCustomViewportHeights = (
  state: StoreShape
): MCCCustomViewportHeights =>
  state.MultiContextCanvasStore.customViewportHeights;

/**
 * Resolves the `vh` base for a breakpoint: a user override when present,
 * otherwise the verified device height.
 */
export const getMCCViewportHeight = (
  breakpointId: BreakpointID,
  customViewportHeights: MCCCustomViewportHeights | undefined
): number =>
  customViewportHeights?.[breakpointId] ?? getBreakpointHeight(breakpointId);

export const selectShouldUseMultiContextCanvas = (
  state: DesignerStores
): boolean => {
  const isUserEnabled = state.MultiContextCanvasStore.userEnabledOpenCanvas;
  const isSystemEnabled =
    state.MultiContextCanvasStore.isEditingPageOnOpenCanvas;
  const canAccess = selectDesignerAbility('canAccessMultiContextCanvas')(state);

  return isUserEnabled && isSystemEnabled && canAccess;
};

/**
 * Returns an object of frame IDs whose content is revealed in the multi-frame view, keyed
 * by frame ID.
 */
export const selectMCCRevealedFrameIds = (
  state: StoreShape
): Record<string, boolean> => state.MultiContextCanvasStore.revealedFrameIds;

/**
 * Returns the performance guardrail policy type for the current page, or `null`
 * when the guardrails status is not yet `settled`.
 */
export const selectMCCGuardrailsPolicyType = (
  state: StoreShape
): MCCGuardrailsPolicyType | null => {
  const {guardrailsStatus} = state.MultiContextCanvasStore;
  return guardrailsStatus.type === 'settled'
    ? guardrailsStatus.policyType
    : null;
};

/**
 * Returns the estimated total rendered-node count behind the current page's
 * guardrail tier, or `null` when the guardrails status is not yet `settled` or
 * the measurement timed out. Never `0` for an unmeasured page, so telemetry can
 * tell "too slow to measure" apart from "an empty page".
 */
export const selectMCCGuardrailsNodeCount = (
  state: StoreShape
): number | null => {
  const {guardrailsStatus} = state.MultiContextCanvasStore;
  return guardrailsStatus.type === 'settled'
    ? (guardrailsStatus.totalNodeCount ?? null)
    : null;
};

/**
 * Returns `true` while MCC evaluates guardrails after a page switch. Used to
 * keep the page-switch overlay visible until the default reveal set is known.
 */
export const selectMCCIsPopulatingFrames = (state: StoreShape): boolean => {
  const {guardrailsStatus, isEditingPageOnOpenCanvas, deferFrameReveal} =
    state.MultiContextCanvasStore;

  const isPopulatingFrames =
    (guardrailsStatus.type === 'pending_render' ||
      guardrailsStatus.type === 'measuring') &&
    deferFrameReveal;
  return isEditingPageOnOpenCanvas && isPopulatingFrames;
};

const selectBreakpoints = (state: DesignerStores) =>
  state.StyleBlockStore.breakpoints;

export const getFramesForAllBreakpoints = (
  frames: Record<string, PageFrame>,
  breakpoints: DesignerStores['StyleBlockStore']['breakpoints']
): PageFrame[] => {
  return MCC_RENDER_ORDER.reduce<PageFrame[]>((res, currBreakpointId) => {
    // The Multi-Context Canvas Store eagerly creates a frame for each possible breakpoint,
    // even if the user hasn't added that breakpoint yet. Here we check if the site actually
    // has that breakpoint added before including it in the list.
    const breakpoint = breakpoints[currBreakpointId];
    if (!breakpoint) return res;

    const frame = frames[mccFrameIdFor(currBreakpointId)];
    if (!frame) return res;

    res.push(frame);
    return res;
  }, []);
};

/**
 * Returns the list of `PageFrame`s ordered by `MCC_RENDER_ORDER`.
 * A `PageFrame` is included in the returned list only if it has a matching
 * breakpoint in the `breakpoints` map.
 */
export const selectMCCFramesForAllBreakpoints = createSelector(
  selectFrames,
  selectBreakpoints,
  getFramesForAllBreakpoints
);

/**
 * World-space x offset of a frame within the ordered all-breakpoints grid.
 */
export const getFrameOffset = (
  orderedFrames: PageFrame[],
  frameId: string
): number => {
  let offset = 0;
  for (const frame of orderedFrames) {
    if (frame.id === frameId) return offset;
    offset += frame.width + MCC_ALL_BREAKPOINTS_GAP_PX;
  }

  return 0;
};

/**
 * Returns the ids of the MCC frames whose page content is currently rendered.
 */
export const selectMCCRenderedFrameIds = createSelector(
  selectCurrentBreakpointFrameId,
  selectMCCFramesForAllBreakpoints,
  selectMCCRevealedFrameIds,
  (activeFrameId, allFrames, revealedFrameIds): readonly string[] => {
    if (allFrames.length > 0) {
      return allFrames
        .filter((frame) => revealedFrameIds[frame.id])
        .map((frame) => frame.id);
    }

    return activeFrameId ? [activeFrameId] : [];
  }
);

/**
 * Returns whether revealing one more enabled frame would exceed the current
 * guardrail policy's reveal budget.
 */
export const selectMCCWouldRevealFrameExceedBudget = createSelector(
  selectMCCFramesForAllBreakpoints,
  selectMCCRevealedFrameIds,
  selectMCCGuardrailsPolicyType,
  (frames, revealedFrameIds, policyType): boolean => {
    if (policyType === null || policyType === 'low') {
      return false;
    }

    const {revealBudget} = getGuardrailPolicy(policyType);
    const revealedEnabledFrameCount = frames.filter(
      (frame) => revealedFrameIds[frame.id]
    ).length;

    return revealedEnabledFrameCount + 1 > revealBudget;
  }
);
